import { AppData, Lightning, Log } from '@lightningjs/sdk'
import { Base, Column } from '@lightningjs/ui-components'
import GridViewModel, { FocusInfo } from './grid-view-model'
import { throttle } from 'throttle-debounce';
import { AppSetup } from '../../app-setup';




export default class MultiRowGrid extends Base<Base.TemplateSpec> {

    listcreated = false
    isTransition = false
    _initialRowIndex = 0
    index = 0
    uiList = [];
    visibleRows = 2;
    bufferRows = 2
    prevTime = 0;

    static override _template(): Lightning.Component.Template<Lightning.Component.TemplateSpecLoose> {
        return {
            ColumnContainer: {
                type: Column,
                alwaysScroll: true,
                autoResizeHieght: false,
                autoResizeWidth: false,
                collision: true,
                items: []
            }
        }
    }

    $vodRowChanged(focusInfo: FocusInfo) {
        this._updateAndFetchMoreRows(focusInfo)
        this.fireAncestors('$gridFocusChange')
        this._grid.changeFocus(focusInfo)
    }

    async _updateAndFetchMoreRows() {
        const nextIndex = Math.min(this._ColumnContainer.selectedIndex + 1, this.uiList.length - 1)
        this._grid.checkAndLoadMoreRows(nextIndex)
    }

    async fetchMoreRows(pgNo: number) {
        return await this._grid.fetchPage(pgNo)
    }

    static get __componentName() {
        return 'MultiRowGrid';
    }

    static get tags() {
        return ['ColumnContainer']
    }

    static get properties() {
        return ['grid']
    }

    override _getFocused() {
        return this._ColumnContainer || null
    }

    clearGrid() {
        if (this._ColumnContainer && this._ColumnContainer.items) {
            this._ColumnContainer.items = []
        }
    }

    override _init() {
        super._init()
        this._ColumnContainer.Items.collision = true,
        this._grid.on('add', this.$add, this)
        this._grid.on('remove', this.$remove, this)
        this._grid.on('update', this.$update, this)
        this._grid.on('refocus', this.$refocus, this)
        this._ColumnContainer.onUp = this.onUp.bind(this)
        this._ColumnContainer.onDown = this.onDown.bind(this)
        this._ColumnContainer.transitionDone = this.transitionDone.bind(this)
        this._updateList()
    }

    _getRowsToDisplay(selectedIndex): { start: number, end: number } {
        let stIndx = Math.max(selectedIndex - this.bufferRows, 0)
        let endIndx = Math.min(stIndx + (this.visibleRows + (this.bufferRows * 2)) - 1, this.uiList.length - 1)
        return { start: stIndx, end: endIndx }
    }

    _updateList(reload = false) {
        this.uiList = this._grid.createGridListUI(reload);
        this._initialRowIndex = this._grid.getFocusInfo().focusRow
        const rowsToDisplay = this._getRowsToDisplay(this._initialRowIndex)
        const visibleList: any = []
        let item;
        for (let i = rowsToDisplay.start; i <= rowsToDisplay.end; i++) {
            if (i >= 0) {
                item = this.uiList[i]
                item.rowIndex = i
                visibleList.push(item)
            }
        }
        this._ColumnContainer.items = [...visibleList]
        this._ColumnContainer.autoResizeHeight = false
        this._ColumnContainer.selectedIndex = this._initialRowIndex - rowsToDisplay.start
        let transitionSetting = {
            timingFunction: 'linear',
            duration: 0.4, //300 ms
            delay: 0
        }
        const { appConfig } = AppData as AppSetup
        if (!appConfig?.enableGridAnimation) {
            transitionSetting.timingFunction = 'step-end';
            transitionSetting.duration = 0.05
        }

        this._ColumnContainer.style.itemTransition = { ...transitionSetting }
        this.index = this._ColumnContainer.selected.rowIndex;
        this._updateAndFetchMoreRows()
    }

    resetFocus(focusInfo: FocusInfo, index = 0) {
        if (this._ColumnContainer.selected.rowIndex !== focusInfo.focusRow) {
            this._updateList()
        } else {
            this._ColumnContainer.selected.data.focusItem = focusInfo.foucsItem
            this._ColumnContainer.selected.updateRowItems()
        }
    }

    reloadGrid() {
        this._updateList(true);
    }

    /**
     * $add - is called with the queue row is added for the first time
     * when the user dose not have queue row.
     * we reload the ui and focus it on the same focus item where user was focused.
     * @param {*} uiRow
     * @param {*} index
     */
    $add(uiRow, index) {
        this._updateList()
    }

    /**
     * $remove - is called with the queue row has only one item and it is removed by the user
     * we reload the ui and focus it on the same focus item where user was focused.
     * @param {*} index
     */
    $remove(index) {
        this._updateList()
    }

    /**
     * $update - is called with the queue row is already available
     * when the user adds or removes from the queue
     * we reload the ui and focus it on the same focus item where user was focused.
     * @param {*} uiRow
     * @param {*} index
     */
    $update(uiRow, index) {
        this._updateList()
        // if (uiRow) {
        //     this._ColumnContainer.items.map(item => {
        //         if (item.rowIndex == index) {
        //             item.updateRowItems()
        //         }
        //     })
        // }
    }

    /**
     * $refoucs - is called with the user wants to go to My Queue
     * @param {*} uiRow
     * @param {*} index
     */
    $refocus(focusInfo, queueUiRowList) {
        this._updateList()
    }

    /**
     * This function will append the next row to the ui list.
     * will be called when onDown is triggered.
     * @param {*} selectedIndex
     */
    appendRowsToBottom(selectedIndex) {
        const nextItemToAdd = this._getRowsToDisplay(selectedIndex + 1)
        const lastAddedItem = this._ColumnContainer.items[this._ColumnContainer.items.length - 1]
        if (lastAddedItem.rowIndex < nextItemToAdd.end) {
            const item = this.uiList[nextItemToAdd.end]
            item.rowIndex = nextItemToAdd.end
            this._ColumnContainer.appendItems([item])
        }
    }

    /**
     * This function will append the previous row to the ui list.
     * will be called when onUp is triggered.
     * @param {*} selectedIndex
     */
    appendRowsToTop(selectedIndex) {
        const prevItemToAdd = this._getRowsToDisplay(selectedIndex - 1)
        const firstAddedItem = this._ColumnContainer.items[0]
        if (firstAddedItem.rowIndex > prevItemToAdd.start) {
            const item = this.uiList[prevItemToAdd.start]
            item.rowIndex = prevItemToAdd.start
            this._ColumnContainer.prependItems([item])
        }
    }

    /**
     * This call back function will be triggered from ColumnContainer
     * when the transition is completed.
     * here we reset the isTransition to false to handle the next key event.
     */
    transitionDone() {
        this.isTransition = false
    }

    $handleEnter() {
        this._grid._dispatchEvent('selected')
    }

    canProcessKey() {
        const { appConfig } = AppData as AppSetup
        if (appConfig?.enableGridAnimation) return true

        const curDt = new Date().getTime()
        if (curDt > (this.prevTime + 400)) {
            this.prevTime = curDt
            return true
        }

        return false
    }

    /**
     * This function is custom function to handle the events from the component.
     * this will ensure the component not to trigger the component default key handling.
     */
    onUp() {
        if (!this.canProcessKey()) return true
        const el = this._ColumnContainer
        if (!this.isTransition && this.uiList.length > 0 && el.selected.rowIndex > 0) {
            this.isTransition = true;
            this.appendRowsToTop(this.index)
            setTimeout(() => {
                el.selectPrevious()
                this.index = el.selected.rowIndex
            }, 16)
            return true
        }
        if (this.parentType == 'playlist' && el.selected.rowIndex == 0) {
            Log.info('Multirow: onUp')
            this.fireAncestors('$handleUp')
            return true
        }
        return true
        //return el.selected.rowIndex == 0 ? false : true
    }

    /**
     * This function is custom function to handle the events from the component.
     * this will ensure the component not to trigger the component default key handling.
     */
    onDown() {
        if (!this.canProcessKey()) return true
        const el = this._ColumnContainer
        if (!this.isTransition && this.uiList.length > 0 && el.selected.rowIndex < this.uiList.length - 1) {
            this.isTransition = true;
            this.appendRowsToBottom(this.index)
            setTimeout(() => {
                el.selectNext()
                this.index = el.selected.rowIndex
            }, 16)
        }
        return true
    }

    _update() {
        if (!this.listcreated) {
            Log.info(MultiRowGrid.__componentName + ' create List:', this._grid)
            //this._updateList()
            this.listcreated = true;
        }
    }

    $updateClickHandle(focusInfo:FocusInfo) {
       // console.log('$updateClickHandle: ', focusInfo, this._grid)
        const grid:GridViewModel = this._grid
        this._ColumnContainer.selectedIndex = focusInfo.focusRow
        grid.changeFocus(focusInfo)
        setTimeout(() => {
            this.$handleEnter()
        },16)
    }

    _handleScroll(obj) {
        //console.log('Multigrid _handleScroll: ',  obj)
        if(obj && obj.wheelDeltaY && obj.wheelDeltaY>0) {
            this.onUp()
        } else {
            this.onDown()
        }
    }
}
